import { publicEnv } from '@/lib/config/public-env';
/**
 * Services for StudioAI feature
 *
 * Instance-based service. Consumers should import the singleton
 * `studioAIService` rather than instantiating the class directly.
 */

import { visibleConversationTitle } from '@/lib/feasibility/dani/primer-marks';
import { ENV } from '@/lib/config/environment';
import { portalApi } from '@/lib/http/axios';
import { getSessionToken } from '@/lib/utils/session';
import { OrganizationProfile, UserProfile } from '@/types';
import { CreditLimitError, UsageLimitError, errorFromChatDenialBody } from './chat-denial';
import {
    API_ENDPOINTS,
    ROLE_TYPES,
    STATUS_MESSAGES,
    STREAMING_EVENT_TYPES,
    STUDIO_AI_NAME,
    TEMP_MESSAGE_IDS,
    UserFeedbackFilter,
} from './constants';
import {
    ApiError,
    ConversationListResponse,
    ConversationShareDetails,
    ConversationsQueryParams,
    CreatePlanTemplateRequest,
    DataRefreshDatesResponseDto,
    DeleteConversationResponse,
    FeedbackDto,
    FreemiumLimitData,
    OrgUserBasic,
    PaginatedConversationsResponse,
    PaginatedPlanTemplatesResponse,
    PaginatedReleaseNotes,
    PaginatedReportsResponse,
    PlanTemplateFinalReportPrompt,
    PlanTemplateResponse,
    ReleaseNote,
    ReportsQueryParams,
    ShareConversationRequest,
    ShareConversationResponse,
    SharedWithMeConversation,
    StudioAIFolder,
    StudioAIReport,
    UserFeedback,
    type StreamingEvent,
    type StreamingHandlers,
} from './types';
import type {
    CreateTemplateStyleRequest,
    PaginatedTemplateStylesResponse,
    TemplateStyleResponse,
    UpdateTemplateStyleRequest,
} from './template-styles';
import { isPendingAiId } from './utils/message-utils';
import { createThinkingRepeatFilter } from './utils/thinking-blocks';
import type { VideoStudy } from '@/lib/reel-studio/agent/blocks';

/** The Reels agent's turn endpoint, a same-origin route in this app. */
const REELS_CHAT_ENDPOINT = '/api/dani/reel-studio/chat';

/** Named SSE events the Reels agent adds; forwarded to the handlers whole. */
const REELS_NAMED_EVENTS: ReadonlySet<string> = new Set([
    STREAMING_EVENT_TYPES.VIDEO_PROGRESS,
    STREAMING_EVENT_TYPES.VIDEO_THEMES,
    STREAMING_EVENT_TYPES.CLIP,
    STREAMING_EVENT_TYPES.VIDEO_STUDY,
]);
import { AxiosInstance } from 'axios';

// ============================================================================
// ERROR CLASSES
// ============================================================================

/**
 * Thrown when the API flags a user message for content moderation.
 * Used to display a user-friendly message explaining the policy violation.
 */
export class ContentModerationError extends Error {
    flaggedCategories: string[];

    constructor(message: string, flaggedCategories: string[] = []) {
        super(message);
        this.name = 'ContentModerationError';
        this.flaggedCategories = flaggedCategories;
    }
}

export class GenerationInProgressError extends Error {
    generatingResponseAt: string;

    constructor(generatingResponseAt: string) {
        super('generation_in_progress');
        this.name = 'GenerationInProgressError';
        this.generatingResponseAt = generatingResponseAt;
    }
}

export { CreditLimitError, UsageLimitError, errorFromChatDenialBody };

// ============================================================================
// STUDIO AI SERVICE
// ============================================================================
export class StudioAIService {
    private readonly axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = portalApi;
    }

    /**
     * Transcribe an audio file using the voice transcription endpoint.
     */
    async transcribeAudio(
        formData: FormData,
    ): Promise<{ text: string; duration?: number; language?: string }> {
        try {
            const response = await this.axiosInstance.post<{
                text: string;
                duration?: number;
                language?: string;
            }>(API_ENDPOINTS.TRANSCRIBE, formData);
            return response.data;
        } catch (error) {
            const message =
                (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Transcription failed';
            throw new Error(message);
        }
    }

    /**
     * Send a message stream using the Next.js API route
     * Processes raw SSE chunks directly similar to backend implementation
     */
    async sendChatMessage(
        message: string,
        threadId: string | null = null,
        parentMessageId: string,
        conversationId: string | null = null,
        handlers: StreamingHandlers = {},
        abortController?: AbortController,
        isPlannerMode: boolean = false,
        contextData?: Record<string, unknown>,
        previousMessages?: Array<{
            role: 'user' | 'assistant';
            content: Array<{ type: string; text: string }>;
        }>,
        masqueradeOrgId?: string,
        masqueradeOrgName?: string,
        chatEndpointOverride?: string,
        isExperimentalAgent?: boolean,
        isSilent?: boolean,
        templateId?: number,
        subsidiaryOrgIds?: number[],
    ): Promise<void> {
        if (!message || !message.trim()) {
            throw new Error('Cannot send an empty message to the AI.');
        }

        const { onError } = handlers;

        try {
            const threadIdStr = threadId || undefined;
            let parentMessageIdStr = parentMessageId || '0';

            if (
                isPendingAiId(parentMessageId) ||
                parentMessageId === TEMP_MESSAGE_IDS.PENDING_USER
            ) {
                parentMessageIdStr = '0';
            }

            const requestBody: Record<string, unknown> = {
                messages: [
                    {
                        role: ROLE_TYPES.USER,
                        content: [
                            {
                                type: 'text',
                                text: message.trim(),
                            },
                        ],
                        ...(isSilent && { silent: true }),
                    },
                ],
                thread_id: threadIdStr,
                parent_message_id: parentMessageIdStr,
                conversationId,
            };

            if (contextData) {
                const { runId, action, editedPrompts, plannerFormState, ...rest } = contextData as {
                    runId?: string;
                    action?: 'plan' | 'run_report';
                    editedPrompts?: Array<Record<string, unknown>>;
                    plannerFormState?: Record<string, unknown>;
                    [key: string]: unknown;
                };
                if (runId != null) {
                    requestBody.runId = runId;
                }
                if (action != null) {
                    requestBody.action = action;
                }
                if (Array.isArray(editedPrompts) && editedPrompts.length > 0) {
                    requestBody.editedPrompts = editedPrompts;
                }
                if (
                    plannerFormState != null &&
                    typeof plannerFormState === 'object' &&
                    Object.keys(plannerFormState).length > 0
                ) {
                    requestBody.plannerFormState = plannerFormState;
                }
                if (Object.keys(rest).length > 0) {
                    requestBody.contextData = rest;
                }
            }
            // `previousMessages` is only used by the legacy `/chat` endpoint
            // for the duplicate-conversation pre-seeding flow. The
            // `/plan-mode/chat` endpoint reads its own conversation history
            // from `studioai_history` (Postgres) and ignores any UI-supplied
            // history, so we never forward it for planner mode.
            if (!isPlannerMode && previousMessages && previousMessages.length > 0) {
                requestBody.previousMessages = previousMessages;
            }
            if (masqueradeOrgId) {
                requestBody.masqueradeOrgId = masqueradeOrgId;
            }
            if (masqueradeOrgName) {
                requestBody.masqueradeOrgName = masqueradeOrgName;
            }
            if (subsidiaryOrgIds?.length) {
                requestBody.subsidiaryOrgIds = subsidiaryOrgIds;
            }
            if (isExperimentalAgent) {
                requestBody.useExperimentalAgent = true;
            }
            if (templateId != null) {
                requestBody.templateId = templateId;
            }

            let chatEndpoint: string;
            if (isPlannerMode) {
                chatEndpoint = API_ENDPOINTS.PLAN_MODE_CHAT;
            } else {
                chatEndpoint = chatEndpointOverride || API_ENDPOINTS.CHAT;
            }
            const portalApiUrl = `${ENV.apiEndpoint}/studio/v1${chatEndpoint}`;

            const token = await getSessionToken({
                serviceName: 'StudioAIService',
                featureName: 'sendChatMessage',
            });

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(portalApiUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(requestBody),
                signal: abortController?.signal,
            });

            if (!response.ok) {
                const errorData = await response.json();

                const meteringError = errorFromChatDenialBody(errorData);
                if (meteringError) {
                    throw meteringError;
                }

                if (errorData?.error === 'Content flagged by moderation') {
                    throw new ContentModerationError(
                        errorData.message ||
                            'Your message was flagged by our content moderation system.',
                        (errorData.flaggedCategories as string[]) || [],
                    );
                }

                // Detect in-progress generation (server is still streaming a previous response)
                if (
                    errorData?.error === 'generation_in_progress' &&
                    errorData?.generatingResponseAt
                ) {
                    throw new GenerationInProgressError(errorData.generatingResponseAt as string);
                }

                // errorData.error is the business-layer message (our custom errors above).
                // errorData.message is the Fastify/NestJS framework-level validation message.
                // Both are surfaced so DTO validation failures are visible.
                const errorMsg = errorData.error || errorData.message;
                throw new Error(
                    typeof errorMsg === 'string'
                        ? errorMsg
                        : Array.isArray(errorMsg)
                          ? errorMsg.join('; ')
                          : `HTTP error! status: ${response.status}`,
                );
            }

            if (!response.body) {
                throw new Error('Response body is empty');
            }

            await StudioAIService.readEventStream(response.body, handlers);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }
            // `onError` is the only consumer — `useChatStreaming.sendMessage`'s
            // mutationFn calls this fire-and-forget (no `await`/`.catch()`,
            // since the real result arrives via `handlers` as SSE events), so
            // a rethrow here becomes a genuinely unhandled rejection. Dev mode
            // surfaces that as a blocking Next.js error overlay; in production
            // it aborts the in-flight `router.push` to the new conversation,
            // which is the "bounced back to the blank landing page" bug this
            // fixes — the mutation's own reject() above already delivers the
            // error through the app's normal error state.
            onError?.(error instanceof Error ? error : new Error('Unknown error'));
        }
    }

    /**
     * Reads a chat SSE stream and hands each event to `handlers`. Shared by the
     * DANI chat and the Reels agent, which speak the same event format.
     */
    private static async readEventStream(
        body: ReadableStream<Uint8Array>,
        handlers: StreamingHandlers,
    ): Promise<void> {
        const { onMessage, onComplete, onHeartbeat } = handlers;
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent: string | null = null;
        let incompleteDataLine = '';
        // The finished reasoning block repeats the pieces already streamed.
        const isRepeatedThinking = createThinkingRepeatFilter();

        while (true) {
            const { value, done } = await reader.read();

            if (done) {
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (!line.trim()) continue;

                // SSE comment lines (e.g. `: ping` keep-alive heartbeats).
                // They carry no event/data payload, but we still want to
                // signal activity so the consumer can reset any idle
                // timeout and keep the StatusMessage visible while Cortex
                // is chewing.
                if (line.startsWith(':')) {
                    onHeartbeat?.();
                    continue;
                }

                // Any non-comment SSE line is also activity from the
                // backend — reset the idle timer before parsing it.
                onHeartbeat?.();

                if (line.startsWith('event: ')) {
                    currentEvent = line.substring(7).trim();
                    continue;
                }

                if (line.startsWith('data: ')) {
                    const data = line.substring(6);

                    if (data === '[DONE]') {
                        incompleteDataLine = '';
                        onComplete?.();
                        return;
                    }

                    const fullData = incompleteDataLine + data;

                    try {
                        const parsedData = StudioAIService.parseJsonWithLargeIntSupport(fullData);

                        incompleteDataLine = '';

                        if (currentEvent === STREAMING_EVENT_TYPES.THREAD_METADATA) {
                            const rawPhase = parsedData.phase;
                            // Historically the agent also emitted a
                            // stale `insights` phase that disabled
                            // polling — we now only surface `report` to
                            // downstream consumers. Other values are
                            // dropped defensively.
                            const phase: 'report' | undefined =
                                rawPhase === 'report' ? rawPhase : undefined;
                            const metadataEvent: StreamingEvent = {
                                type: STREAMING_EVENT_TYPES.THREAD_METADATA,
                                threadId: parsedData.thread_id?.toString(),
                                conversationId: parsedData.conversation_id as string,
                                runId: parsedData.run_id ? String(parsedData.run_id) : undefined,
                                phase,
                            };
                            onMessage?.(metadataEvent);
                            currentEvent = null;
                            continue;
                        }

                        if (currentEvent === STREAMING_EVENT_TYPES.AGENT_OPTIONS) {
                            const optionsPayload =
                                typeof parsedData === 'string'
                                    ? JSON.parse(parsedData)
                                    : parsedData;
                            const optionsEvent: StreamingEvent = {
                                type: STREAMING_EVENT_TYPES.AGENT_OPTIONS,
                                agent_options: optionsPayload,
                            };
                            onMessage?.(optionsEvent);
                            currentEvent = null;
                            continue;
                        }

                        if (currentEvent === STREAMING_EVENT_TYPES.PLAN_SUMMARY) {
                            const payload =
                                typeof parsedData === 'string'
                                    ? JSON.parse(parsedData)
                                    : parsedData;
                            onMessage?.({
                                type: STREAMING_EVENT_TYPES.PLAN_SUMMARY,
                                content: JSON.stringify(payload),
                            });
                            currentEvent = null;
                            continue;
                        }

                        // Live plan-sidebar snapshot, latest wins. Forwarded
                        // to the streaming handler which dispatches it into
                        // state without mutating message content.
                        if (currentEvent === STREAMING_EVENT_TYPES.PLAN_PROGRESS) {
                            const payload =
                                typeof parsedData === 'string'
                                    ? JSON.parse(parsedData)
                                    : parsedData;
                            onMessage?.({
                                type: STREAMING_EVENT_TYPES.PLAN_PROGRESS,
                                content: JSON.stringify(payload),
                            });
                            currentEvent = null;
                            continue;
                        }

                        if (currentEvent === STREAMING_EVENT_TYPES.REPORT_UPDATED) {
                            const payload =
                                typeof parsedData === 'string'
                                    ? JSON.parse(parsedData)
                                    : parsedData;
                            const rawReportId =
                                typeof payload === 'object' && payload !== null
                                    ? (payload as Record<string, unknown>).report_id
                                    : undefined;
                            const reportId =
                                typeof rawReportId === 'number' ? rawReportId : Number(rawReportId);
                            onMessage?.({
                                type: STREAMING_EVENT_TYPES.REPORT_UPDATED,
                                content: JSON.stringify(payload),
                                reportId: Number.isFinite(reportId) ? reportId : undefined,
                            });
                            currentEvent = null;
                            continue;
                        }

                        // Reels agent blocks and the attached study arrive as
                        // named events with their payload as data, like
                        // plan_summary, and are forwarded whole.
                        if (currentEvent && REELS_NAMED_EVENTS.has(currentEvent)) {
                            onMessage?.({
                                type: currentEvent as StreamingEvent['type'],
                                content: JSON.stringify(parsedData),
                            });
                            currentEvent = null;
                            continue;
                        }

                        // All other events — including Cortex-format
                        // `response.text.delta` (text chunks) and the
                        // `data:`-only delta envelopes for thinking —
                        // flow through the shared transformEventData
                        // path so the same handler covers Cortex and
                        // the planner agent.
                        if (isRepeatedThinking(currentEvent, parsedData)) {
                            currentEvent = null;
                            continue;
                        }

                        const streamingEvent: StreamingEvent =
                            StudioAIService.transformEventData(parsedData);
                        onMessage?.(streamingEvent);
                    } catch (parseError) {
                        // JSON parse errors can happen with large payloads split across chunks
                        if (parseError instanceof SyntaxError) {
                            incompleteDataLine = fullData;
                            continue;
                        } else {
                            incompleteDataLine = '';
                        }
                    }

                    currentEvent = null;
                }
            }
        }

        onComplete?.();
    }

    /**
     * One turn of the Reels agent (`POST /api/dani/reel-studio/chat`), for a
     * conversation about a video study. Same-origin, so the session cookie
     * authenticates it; the answer streams in the chat's own event format.
     */
    async sendReelsChatMessage(
        request: {
            message: string;
            conversationId: string | null;
            study: VideoStudy | null;
            history: Array<{ role: 'user' | 'assistant'; text: string }>;
        },
        handlers: StreamingHandlers = {},
        abortController?: AbortController,
    ): Promise<void> {
        try {
            const response = await fetch(REELS_CHAT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: request.message,
                    conversationId: request.conversationId,
                    history: request.history,
                    study: request.study
                        ? {
                              projectId: request.study.projectId,
                              surveyIds: request.study.surveyIds,
                              questionId: request.study.questionId,
                          }
                        : null,
                }),
                signal: abortController?.signal,
            });
            if (!response.ok) {
                const errorData = (await response.json().catch(() => ({}))) as { error?: string };
                // No DANI access (403) or too many turns (429): the route's own
                // copy is the reply, shown in the conversation like an answer.
                if ((response.status === 403 || response.status === 429) && errorData.error) {
                    handlers.onMessage?.({
                        type: STREAMING_EVENT_TYPES.CONTENT_ARRAY,
                        content: JSON.stringify([
                            { type: STREAMING_EVENT_TYPES.TEXT, content: errorData.error },
                        ]),
                    });
                    handlers.onMessage?.({ type: STREAMING_EVENT_TYPES.DONE });
                    return;
                }
                throw new Error(
                    response.status === 404
                        ? 'That video study could not be found among your studies.'
                        : errorData.error || `HTTP error! status: ${response.status}`,
                );
            }
            if (!response.body) throw new Error('Response body is empty');
            await StudioAIService.readEventStream(response.body, handlers);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;
            handlers.onError?.(error instanceof Error ? error : new Error('Unknown error'));
        }
    }

    /**
     * Parse JSON while preserving large integer precision.
     * Wraps known large-integer fields (message_id etc.) in quotes before
     * `JSON.parse` so JS Number coercion doesn't lose precision.
     */
    private static parseJsonWithLargeIntSupport(jsonString: string): Record<string, unknown> {
        const largeIntRegex = /"(message_id|tool_use_id|thread_id|run_id)":(\d{15,})/g;
        const processedJson = jsonString.replace(largeIntRegex, '"$1":"$2"');

        return JSON.parse(processedJson);
    }

    /**
     * Transform Snowflake Cortex response format to our internal format.
     * Pure function — callable without an instance handle so the streaming
     * pipeline can invoke it directly.
     */
    static transformEventData(data: Record<string, unknown>): StreamingEvent {
        // Check for errors first (including Snowflake API errors)
        if (data.code && data.message) {
            return {
                type: STREAMING_EVENT_TYPES.ERROR,
                message: data.message as string,
                content: data.message as string,
                code: data.code as string,
                request_id: data.request_id as string,
            };
        }

        // Handle metadata events (message_id, role, etc.)
        // IMPORTANT: Check for content FIRST before returning for metadata-only events.
        const hasContent =
            data.content &&
            Array.isArray(data.content) &&
            (data.role === ROLE_TYPES.ASSISTANT || data.schema_version === 'v2');

        if (data.metadata && typeof data.metadata === 'object') {
            if (!hasContent) {
                const metadata = data.metadata as Record<string, unknown>;
                const result: StreamingEvent = {
                    type: STREAMING_EVENT_TYPES.METADATA,
                };

                if ('message_id' in metadata) {
                    result.messageId = metadata.message_id
                        ? String(metadata.message_id)
                        : undefined;
                    result.content = JSON.stringify(metadata);
                }

                if ('usage' in metadata) {
                    result.usage = metadata.usage as Record<string, unknown>;
                }

                if (result.messageId !== undefined || result.usage !== undefined) {
                    return result;
                }
            }
        }

        if (hasContent) {
            const contentArray = data.content as Array<Record<string, unknown>>;

            const allContent: Array<{
                type: string;
                content?: string;
                thinking?: string;
                chart?: Record<string, unknown>;
                table?: Record<string, unknown>;
                tool_use?: Record<string, unknown>;
                tool_result?: Record<string, unknown>;
                sql?: string;
                plan_summary?: Record<string, unknown>;
                agent_options?: Record<string, unknown>;
                video_progress?: Record<string, unknown>;
                video_themes?: Record<string, unknown>;
                clip?: Record<string, unknown>;
            }> = [];

            contentArray.forEach((item) => {
                switch (item.type) {
                    case STREAMING_EVENT_TYPES.THINKING: {
                        // Thinking text can live at item.thinking.text or item.text.
                        let thinkingText = '';
                        if (item.thinking && typeof item.thinking === 'object') {
                            const thinkingObj = item.thinking as Record<string, unknown>;
                            thinkingText = thinkingObj.text as string;
                        } else if (item.text && typeof item.text === 'string') {
                            thinkingText = item.text;
                        }

                        if (thinkingText) {
                            allContent.push({
                                type: STREAMING_EVENT_TYPES.THINKING,
                                content: thinkingText,
                            });
                        }
                        break;
                    }
                    case STREAMING_EVENT_TYPES.TEXT: {
                        // Text can live at item.text (string), item.text.text (nested),
                        // or item.content as a fallback.
                        let textContent = '';
                        if (typeof item.text === 'string') {
                            textContent = item.text;
                        } else if (item.text && typeof item.text === 'object') {
                            const textObj = item.text as Record<string, unknown>;
                            if (typeof textObj.text === 'string') {
                                textContent = textObj.text;
                            }
                        } else if (typeof item.content === 'string') {
                            textContent = item.content;
                        }

                        if (textContent) {
                            allContent.push({
                                type: STREAMING_EVENT_TYPES.TEXT,
                                content: textContent,
                            });
                        }
                        break;
                    }
                    case STREAMING_EVENT_TYPES.CHART:
                        allContent.push({
                            type: STREAMING_EVENT_TYPES.CHART,
                            chart: item.chart as Record<string, unknown>,
                        });
                        break;
                    case STREAMING_EVENT_TYPES.TABLE:
                        allContent.push({
                            type: STREAMING_EVENT_TYPES.TABLE,
                            table: item.table as Record<string, unknown>,
                        });
                        break;
                    case STREAMING_EVENT_TYPES.TOOL_USE:
                        allContent.push({
                            type: STREAMING_EVENT_TYPES.TOOL_USE,
                            tool_use: item.tool_use as Record<string, unknown>,
                        });
                        break;
                    case STREAMING_EVENT_TYPES.TOOL_RESULT:
                        allContent.push({
                            type: STREAMING_EVENT_TYPES.TOOL_RESULT,
                            tool_result: item.tool_result as Record<string, unknown>,
                        });
                        break;
                    case STREAMING_EVENT_TYPES.PLAN_SUMMARY:
                        allContent.push({
                            type: STREAMING_EVENT_TYPES.PLAN_SUMMARY,
                            plan_summary: item.plan_summary as Record<string, unknown>,
                        });
                        break;
                    case STREAMING_EVENT_TYPES.AGENT_OPTIONS:
                        allContent.push({
                            type: STREAMING_EVENT_TYPES.AGENT_OPTIONS,
                            agent_options: item.agent_options as Record<string, unknown>,
                        });
                        break;
                    // Reels agent blocks: kept whole, keyed by their own type.
                    case STREAMING_EVENT_TYPES.VIDEO_PROGRESS:
                    case STREAMING_EVENT_TYPES.VIDEO_THEMES:
                    case STREAMING_EVENT_TYPES.CLIP:
                        allContent.push({
                            type: item.type,
                            [item.type]: item[item.type] as Record<string, unknown>,
                        });
                        break;
                    default:
                        break;
                }
            });

            const result: StreamingEvent = {
                type: STREAMING_EVENT_TYPES.CONTENT_ARRAY,
                content: JSON.stringify(allContent),
            };

            if (data.metadata && typeof data.metadata === 'object' && 'usage' in data.metadata) {
                const metadata = data.metadata as Record<string, unknown>;
                result.usage = metadata.usage as Record<string, unknown>;
            }

            return result;
        }

        // Status messages — surface user-friendly feedback for known phases.
        if (data.status && data.message) {
            const message = data.message as string;
            const status = data.status as string;

            const getStatusMapping = (message: string, status: string) => {
                if (status === 'processing_answer') {
                    return STATUS_MESSAGES.PROCESSING;
                }

                if (message.toLowerCase().includes('planning') || status === 'planning') {
                    return STATUS_MESSAGES.PLANNING;
                }

                if (message.toLowerCase().includes('thinking') || status === 'thinking') {
                    return STATUS_MESSAGES.THINKING;
                }

                if (
                    message.toLowerCase().includes('choosing data') ||
                    message.toLowerCase().includes('getting additional context') ||
                    status === 'extracting_tool_calls'
                ) {
                    return STATUS_MESSAGES.FINDING_DATA;
                }

                if (
                    message.toLowerCase().includes('interpreting') ||
                    status === 'interpreting_question'
                ) {
                    return STATUS_MESSAGES.UNDERSTANDING_REQUEST;
                }

                if (
                    message.toLowerCase().includes('generating sql') ||
                    status === 'generating_sql'
                ) {
                    return STATUS_MESSAGES.WRITING_QUERY;
                }

                if (
                    message.toLowerCase().includes('postprocessing') ||
                    status === 'postprocessing_sql'
                ) {
                    return STATUS_MESSAGES.OPTIMIZING_QUERY;
                }

                if (message.toLowerCase().includes('executing sql') || status === 'Executing SQL') {
                    return STATUS_MESSAGES.GATHERING_DATA;
                }

                if (
                    message.toLowerCase().includes('reviewing') ||
                    status === 'reasoning_agent_stop'
                ) {
                    return STATUS_MESSAGES.ANALYZING_RESULTS;
                }

                if (
                    message.toLowerCase().includes('rethinking') ||
                    status === 'reevaluating_plan'
                ) {
                    return STATUS_MESSAGES.REFINING_APPROACH;
                }

                if (
                    message.toLowerCase().includes('forming') ||
                    status === 'proceeding_to_answer'
                ) {
                    return STATUS_MESSAGES.PREPARING_ANSWER;
                }

                // Generic runtime progress: forward the raw message verbatim
                // with a spinner icon. This is what lets the planner agent's
                // emit_response_status('running', '...') frames (e.g.
                // "Analyzing audience_profile...", "Retrying...",
                // "Assembling insights...") surface live in StatusMessage
                // without having to match one of the keyword-based branches
                // above.
                if (status === 'running' && message) {
                    return { message, icon: 'lucide:loader-2' };
                }

                return null;
            };

            const mapping = getStatusMapping(message, status);
            if (mapping) {
                return {
                    type: STREAMING_EVENT_TYPES.STATUS,
                    content: mapping.message,
                    messageId: mapping.icon, // Use messageId to pass icon
                };
            }

            // Filter out other internal messages and tool-specific statuses.
            if (
                message.includes('Running') ||
                message.includes('Streaming') ||
                message.includes('Done') ||
                status.includes('tool')
            ) {
                return {
                    type: STREAMING_EVENT_TYPES.TEXT,
                    content: '',
                };
            }

            return {
                type: STREAMING_EVENT_TYPES.TEXT,
                content: '',
            };
        }

        if (data.content_index !== undefined && data.text !== undefined) {
            return {
                type: STREAMING_EVENT_TYPES.TEXT,
                content: data.text as string,
            };
        }

        if (data.delta) {
            const delta = data.delta as Record<string, unknown>;

            if (!delta.content || !Array.isArray(delta.content)) {
                return {
                    type: STREAMING_EVENT_TYPES.TEXT,
                    content: '',
                };
            }

            const contentArray = delta.content as Array<Record<string, unknown>>;
            if (contentArray.length === 0) {
                return {
                    type: STREAMING_EVENT_TYPES.TEXT,
                    content: '',
                };
            }

            const firstContent = contentArray[0];
            const { type, text } = firstContent;

            if (type === STREAMING_EVENT_TYPES.TEXT && text !== undefined) {
                return {
                    type: STREAMING_EVENT_TYPES.TEXT,
                    content: text as string,
                };
            } else if (type === STREAMING_EVENT_TYPES.TOOL_USE) {
                return {
                    type: STREAMING_EVENT_TYPES.TOOL_USE,
                    tool_use: firstContent.tool_use as Record<string, unknown>,
                    messageId: firstContent.tool_use_id as string,
                };
            } else if (
                contentArray.length > 1 ||
                (contentArray.length === 1 && type !== STREAMING_EVENT_TYPES.TEXT)
            ) {
                return {
                    type: STREAMING_EVENT_TYPES.DELTA_CONTENT,
                    content: JSON.stringify(contentArray),
                };
            }
        }

        if (data.type === STREAMING_EVENT_TYPES.DONE) {
            return {
                type: STREAMING_EVENT_TYPES.DONE,
            };
        }

        return {
            type: STREAMING_EVENT_TYPES.TEXT,
            content: '',
        };
    }

    /**
     * Get the active DANI config metadata (e.g. infoCautionToolNames) for the current user.
     * The backend resolves the correct config based on the user's internal/external status.
     */
    async getActiveConfig(
        useFreemiumEndpoint = false,
    ): Promise<{ infoCautionToolNames: string[] }> {
        try {
            const endpoint = useFreemiumEndpoint
                ? API_ENDPOINTS.FREEMIUM_ACTIVE_CONFIG
                : API_ENDPOINTS.ACTIVE_CONFIG;
            const response = await this.axiosInstance.get<{ infoCautionToolNames: string[] }>(
                endpoint,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get chat conversations for the current user with optional limit and search
     */
    async getConversations(
        params?: ConversationsQueryParams,
    ): Promise<PaginatedConversationsResponse> {
        try {
            const response = await this.axiosInstance.get<PaginatedConversationsResponse>(
                API_ENDPOINTS.CONVERSATIONS,
                { params },
            );
            // A feasibility chat is titled after its hidden first message: show a plain name.
            return {
                ...response.data,
                data: (response.data.data ?? []).map((c) => {
                    const title = visibleConversationTitle(c.title);
                    return title === c.title ? c : { ...c, title: title ?? c.title };
                }),
            };
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get conversation history for a specific conversation
     */
    async getConversationHistory(conversationId: string): Promise<ConversationListResponse> {
        try {
            const response = await this.axiosInstance.get<ConversationListResponse>(
                `${API_ENDPOINTS.CONVERSATION_HISTORY}/${conversationId}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Delete a chat conversation
     */
    async deleteConversation(conversationId: string): Promise<DeleteConversationResponse> {
        try {
            const response = await this.axiosInstance.delete<DeleteConversationResponse>(
                `${API_ENDPOINTS.DELETE_CONVERSATION}/${conversationId}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async stopConversation(conversationId: string): Promise<void> {
        try {
            await portalApi.post(`${API_ENDPOINTS.STOP_CONVERSATION}/${conversationId}/stop`);
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get unread feedback count
     */
    async getUnreadFeedbackCount(): Promise<number> {
        try {
            const response = await this.axiosInstance.get<{ count: number }>(
                `${API_ENDPOINTS.FEEDBACK}/unread-count`,
            );
            return response.data.count;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Mark feedback as read
     */
    async markFeedbackAsRead(feedbackId: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.put<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.FEEDBACK}/${feedbackId}/read`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get user feedback with optional filtering
     */
    async getUserFeedback(
        filter: UserFeedbackFilter = UserFeedbackFilter.ALL,
    ): Promise<UserFeedback[]> {
        try {
            const response = await this.axiosInstance.get<UserFeedback[]>(API_ENDPOINTS.FEEDBACK, {
                params: { filter },
            });
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getDataRefreshDates(): Promise<DataRefreshDatesResponseDto> {
        try {
            const response = await this.axiosInstance.get<DataRefreshDatesResponseDto>(
                API_ENDPOINTS.DATA_REFRESH_DATES,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Rename a conversation
     */
    async renameConversation(
        conversationId: string,
        newTitle: string,
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.put<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.CONVERSATIONS}/${conversationId}/rename`,
                { title: newTitle },
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Submit feedback
     */
    async submitFeedback(
        feedback: FeedbackDto,
        conversationContext?: {
            userMessage?: string;
            aiResponse?: string;
        },
        user?: UserProfile,
        org?: OrganizationProfile,
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; message: string }>(
                API_ENDPOINTS.FEEDBACK,
                feedback,
            );

            const { alertSummary, longDetails, snippetTitle } =
                StudioAIService.createFeedbackSlackAlert(feedback, conversationContext, user, org);

            const channelId = publicEnv('NEXT_PUBLIC_SLACK_FEEDBACK_CHANNEL_ID');
            const slackBody = {
                alertSummary,
                longDetails,
                snippetTitle,
                channelId,
            };

            if (ENV.isProduction) {
                await fetch('/api/slack/post-alert', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(slackBody),
                });
            }

            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Create formatted Slack alert for feedback.
     * Pure formatter — no axios dependency, so callable without an instance.
     */
    private static createFeedbackSlackAlert(
        feedback: FeedbackDto,
        conversationContext?: {
            userMessage?: string;
            aiResponse?: string;
        },
        user?: UserProfile,
        org?: OrganizationProfile,
    ): {
        alertSummary: string;
        longDetails: string;
        snippetTitle: string;
    } {
        const timestamp = new Date().toISOString();
        const feedbackType = feedback.isPositive ? '👍 POSITIVE' : '👎 NEGATIVE';
        const category = feedback.category || 'No category specified';

        const alertSummary = `${STUDIO_AI_NAME} Feedback Alert - ${feedbackType}`;

        const longDetails = `
${STUDIO_AI_NAME} FEEDBACK ALERT
========================

TIMESTAMP: ${timestamp}
FEEDBACK TYPE: ${feedbackType}
CATEGORY: ${category} (No categories for feedback implemented yet)

CONVERSATION DETAILS:
--------------------
Thread ID: ${feedback.threadId}
Message ID: ${feedback.messageId}
Conversation ID: ${feedback.conversationId}

USER DETAILS:
-------------
User ID: ${user?.id}
User Email: ${user?.email}
Organization ID: ${org?.orgId}
Organization Name: ${org?.orgName}

USER MESSAGE:
------------
${conversationContext?.userMessage || 'No user message available'}

AI RESPONSE:
------------
${conversationContext?.aiResponse || 'No AI response available'}

FEEDBACK MESSAGE:
----------------
${feedback.feedbackMessage}

------------------
This alert was automatically generated from ${STUDIO_AI_NAME} feedback submission.
        `.trim();

        const snippetTitle = `studio-ai-feedback-${feedback.isPositive ? 'positive' : 'negative'}-${Date.now()}.txt`;

        return {
            alertSummary,
            longDetails,
            snippetTitle,
        };
    }

    // =========================================================================
    // FOLDER MANAGEMENT
    // =========================================================================

    /**
     * Get all folders for the current user
     */
    async getFolders(): Promise<StudioAIFolder[]> {
        try {
            const response = await this.axiosInstance.get<StudioAIFolder[]>(API_ENDPOINTS.FOLDERS);
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Create a new folder
     */
    async createFolder(name: string, folderType: string = 'conversation'): Promise<StudioAIFolder> {
        try {
            const response = await this.axiosInstance.post<StudioAIFolder>(API_ENDPOINTS.FOLDERS, {
                name,
                folderType,
            });
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Rename a folder
     */
    async renameFolder(
        folderId: number,
        newName: string,
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.put<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.FOLDERS}/${folderId}`,
                { name: newName },
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Delete a folder
     */
    async deleteFolder(folderId: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.delete<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.FOLDERS}/${folderId}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Move a conversation to a folder (or remove from folder by passing null)
     */
    async moveConversationToFolder(
        conversationId: string,
        folderId: number | null,
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.put<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.CONVERSATIONS}/${conversationId}/folder`,
                { folderId },
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    // =========================================================================
    // CONVERSATION SHARING
    // =========================================================================

    /**
     * Get users in the same organization
     */
    async getOrganizationUsers(): Promise<OrgUserBasic[]> {
        try {
            const response = await this.axiosInstance.get<OrgUserBasic[]>(
                API_ENDPOINTS.ORGANIZATION_USERS,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Share a conversation
     */
    async shareConversation(
        conversationId: string,
        request: ShareConversationRequest,
    ): Promise<ShareConversationResponse> {
        try {
            const response = await this.axiosInstance.post<ShareConversationResponse>(
                `${API_ENDPOINTS.SHARE_CONVERSATION}/${conversationId}/share`,
                request,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get conversations shared with the current user
     */
    async getSharedWithMe(): Promise<SharedWithMeConversation[]> {
        try {
            const response = await this.axiosInstance.get<SharedWithMeConversation[]>(
                API_ENDPOINTS.SHARED_WITH_ME,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get share details for a conversation
     */
    async getConversationShares(conversationId: string): Promise<ConversationShareDetails> {
        try {
            const response = await this.axiosInstance.get<ConversationShareDetails>(
                `${API_ENDPOINTS.SHARE_CONVERSATION}/${conversationId}/shares`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Revoke a share
     */
    async revokeShare(shareId: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.delete<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.SHARES}/${shareId}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get all published release notes for the current user (including previously acknowledged)
     */
    async getAllReleaseNotes(
        page: number = 1,
        limit: number = 10,
        search?: string,
        filter?: 'all' | 'new' | 'acknowledged' | 'internal',
        area?: string,
    ): Promise<PaginatedReleaseNotes> {
        try {
            const response = await this.axiosInstance.get<PaginatedReleaseNotes>(
                API_ENDPOINTS.RELEASE_NOTES_ALL,
                {
                    params: {
                        page,
                        limit,
                        ...(search ? { search } : {}),
                        ...(filter && filter !== 'all' ? { filter } : {}),
                        ...(area ? { area } : {}),
                    },
                },
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Get pending release notes for the current user
     */
    async getPendingReleaseNotes(): Promise<ReleaseNote[]> {
        try {
            const response = await this.axiosInstance.get<ReleaseNote[]>(
                API_ENDPOINTS.RELEASE_NOTES_PENDING,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Acknowledge all pending release notes
     */
    async acknowledgeReleaseNotes(
        version?: string,
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; message: string }>(
                API_ENDPOINTS.RELEASE_NOTES_ACKNOWLEDGE,
                version ? { version } : {},
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Dismiss release notes (tracks for analytics, notes reappear on next session)
     */
    async dismissReleaseNotes(): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.post<{ success: boolean; message: string }>(
                API_ENDPOINTS.RELEASE_NOTES_DISMISS,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getAccessStatus(): Promise<{
        hasAccess: boolean;
        freemiumUsage: FreemiumLimitData;
        clientStudioAIAccess?: {
            hasAccess: boolean;
            limitType: 'message' | 'token' | null;
            limitValue: number | null;
            windowDays: number | null;
            currentCount: number;
            hasReachedLimit: boolean;
            daysUntilReset: number | null;
            limitMessage: string | null;
        };
    }> {
        try {
            const response = await this.axiosInstance.get<{
                hasAccess: boolean;
                freemiumUsage: FreemiumLimitData;
                clientStudioAIAccess?: {
                    hasAccess: boolean;
                    limitType: 'message' | 'token' | null;
                    limitValue: number | null;
                    windowDays: number | null;
                    currentCount: number;
                    hasReachedLimit: boolean;
                    daysUntilReset: number | null;
                    limitMessage: string | null;
                };
            }>(API_ENDPOINTS.ACCESS_STATUS);
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async exportQueryData(
        conversationId: string,
        messageId: string,
    ): Promise<{ blob: Blob; truncated: boolean; rowCount: number }> {
        try {
            const response = await this.axiosInstance.post<Blob>(
                API_ENDPOINTS.EXPORT_QUERY_DATA,
                { conversationId, messageId },
                { timeout: 5 * 60 * 1000, responseType: 'blob' },
            );
            const header = (name: string): string => {
                const value = response.headers?.[name];
                return value == null ? '' : String(value);
            };
            return {
                blob: response.data,
                truncated: header('x-export-truncated').toLowerCase() === 'true',
                rowCount: Number(header('x-export-row-count')) || 0,
            };
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getReportStatus(
        runId: string,
    ): Promise<{ status: string; reportId?: number; reportUrl?: string }> {
        const token = await getSessionToken({
            serviceName: 'StudioAIService',
            featureName: 'getReportStatus',
        });
        const url = `${ENV.apiEndpoint}/studio/v1/studio-ai/plan-mode/status/${runId}`;
        const res = await fetch(url, {
            headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
            },
        });
        if (!res.ok) {
            throw new Error(`Status check failed: ${res.status}`);
        }
        return res.json();
    }

    async getReports(params?: ReportsQueryParams): Promise<PaginatedReportsResponse> {
        try {
            const response = await this.axiosInstance.get<
                PaginatedReportsResponse | StudioAIReport[]
            >(API_ENDPOINTS.REPORTS, { params });
            const payload = response.data;
            if (Array.isArray(payload)) {
                const totalUnfiled = payload.filter((r) => r.folderId == null).length;
                return {
                    data: payload,
                    totalUnfiled,
                    page: 1,
                    limit: payload.length,
                };
            }
            return payload;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getReportHtml(
        reportId: number,
    ): Promise<{ htmlContent: string; conversationId: string; title?: string }> {
        try {
            const response = await this.axiosInstance.get<{
                htmlContent: string;
                conversationId: string;
                title?: string;
            }>(`${API_ENDPOINTS.REPORTS}/${reportId}`);
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getReportSlides(reportId: number): Promise<{
        title: string;
        slides: import('@/@studio-ai/components/slide-editor/types').Slide[];
    }> {
        const res = await fetch(`/api/reports/${reportId}/slides`);
        if (!res.ok) throw new Error(`Failed to fetch slides: ${res.status}`);
        return res.json() as Promise<{
            title: string;
            slides: import('@/@studio-ai/components/slide-editor/types').Slide[];
        }>;
    }

    async exportReport(
        reportId: number,
        format: 'pdf' | 'pptx',
    ): Promise<{ url: string; filename: string; expiresIn: number }> {
        try {
            const response = await this.axiosInstance.post<{
                url: string;
                filename: string;
                expiresIn: number;
            }>(`${API_ENDPOINTS.REPORTS}/${reportId}/export`, { format }, { timeout: 200_000 });
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    // =========================================================================
    // PLAN TEMPLATES
    // =========================================================================

    async getPlanTemplates(): Promise<PlanTemplateResponse[]> {
        try {
            const response = await this.axiosInstance.get<PlanTemplateResponse[]>(
                API_ENDPOINTS.PLAN_TEMPLATES,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getAdminPlanTemplates(params?: {
        page?: number;
        limit?: number;
        visibleOnly?: boolean;
    }): Promise<PaginatedPlanTemplatesResponse> {
        try {
            const response = await this.axiosInstance.get<PaginatedPlanTemplatesResponse>(
                API_ENDPOINTS.ADMIN_PLAN_TEMPLATES,
                { params },
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getAdminPlanTemplate(id: number): Promise<PlanTemplateResponse> {
        try {
            const response = await this.axiosInstance.get<PlanTemplateResponse>(
                `${API_ENDPOINTS.ADMIN_PLAN_TEMPLATES}/${id}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async createPlanTemplate(dto: CreatePlanTemplateRequest): Promise<PlanTemplateResponse> {
        try {
            const response = await this.axiosInstance.post<PlanTemplateResponse>(
                API_ENDPOINTS.ADMIN_PLAN_TEMPLATES,
                dto,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async updatePlanTemplate(
        id: number,
        dto: Partial<CreatePlanTemplateRequest>,
    ): Promise<PlanTemplateResponse> {
        try {
            const response = await this.axiosInstance.put<PlanTemplateResponse>(
                `${API_ENDPOINTS.ADMIN_PLAN_TEMPLATES}/${id}`,
                dto,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async deletePlanTemplate(id: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.delete<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.ADMIN_PLAN_TEMPLATES}/${id}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Returns the curated planner prompts persisted on a conversation's
     * `plan_progress.prompts` JSON column, in the same shape used by
     * `studioai_plan_templates.final_report_prompts`. The Plan Template
     * form modal calls this when an admin enters a conversation id and
     * clicks "Search" to seed a starting prompt list.
     */
    async getPlanTemplatePromptsFromConversation(
        conversationId: string,
    ): Promise<PlanTemplateFinalReportPrompt[]> {
        try {
            const response = await this.axiosInstance.get<PlanTemplateFinalReportPrompt[]>(
                `${API_ENDPOINTS.ADMIN_PLAN_TEMPLATE_PROMPTS_FROM_CONVERSATION}/${encodeURIComponent(
                    conversationId,
                )}/prompts`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    // =========================================================================
    // TEMPLATE STYLES (admin)
    // =========================================================================
    //
    // CRUD over the markdown style sheets used by the planner agent's HTML
    // report builder. The Plan Template Styles admin page is the primary
    // consumer; the plan-template form modal uses ``getAdminTemplateStyles``
    // to populate the "Report style" dropdown.

    async getAdminTemplateStyles(params?: {
        page?: number;
        limit?: number;
        visibleOnly?: boolean;
    }): Promise<PaginatedTemplateStylesResponse> {
        try {
            const response = await this.axiosInstance.get<PaginatedTemplateStylesResponse>(
                API_ENDPOINTS.ADMIN_TEMPLATE_STYLES,
                { params },
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async getAllAdminTemplateStyles(params?: {
        visibleOnly?: boolean;
    }): Promise<TemplateStyleResponse[]> {
        const limit = 100;
        const styles: TemplateStyleResponse[] = [];
        let page = 1;

        while (true) {
            const response = await this.getAdminTemplateStyles({
                ...params,
                page,
                limit,
            });

            styles.push(...response.data);

            if (page >= response.pagination.totalPages) {
                return styles;
            }

            page += 1;
        }
    }

    async getAdminTemplateStyle(id: number): Promise<TemplateStyleResponse> {
        try {
            const response = await this.axiosInstance.get<TemplateStyleResponse>(
                `${API_ENDPOINTS.ADMIN_TEMPLATE_STYLES}/${id}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async createTemplateStyle(dto: CreateTemplateStyleRequest): Promise<TemplateStyleResponse> {
        try {
            const response = await this.axiosInstance.post<TemplateStyleResponse>(
                API_ENDPOINTS.ADMIN_TEMPLATE_STYLES,
                dto,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async updateTemplateStyle(
        id: number,
        dto: UpdateTemplateStyleRequest,
    ): Promise<TemplateStyleResponse> {
        try {
            const response = await this.axiosInstance.put<TemplateStyleResponse>(
                `${API_ENDPOINTS.ADMIN_TEMPLATE_STYLES}/${id}`,
                dto,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    async deleteTemplateStyle(id: number): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.delete<{ success: boolean; message: string }>(
                `${API_ENDPOINTS.ADMIN_TEMPLATE_STYLES}/${id}`,
            );
            return response.data;
        } catch (error) {
            throw StudioAIService.handleError(error);
        }
    }

    /**
     * Normalize an axios error (or any thrown value) into a plain `Error`
     * with a user-readable message. All instance methods funnel rejections
     * through here so consumers see a consistent shape.
     */
    private static handleError(error: unknown): Error {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { data?: ApiError; status?: number } };
            const apiError: ApiError = axiosError.response?.data || {
                statusCode: axiosError.response?.status || 500,
                message: 'An unexpected error occurred',
                error: 'Internal Server Error',
            };

            const message = Array.isArray(apiError.message)
                ? apiError.message.join(', ')
                : apiError.message;

            return new Error(message);
        }

        if (error instanceof Error) {
            return error;
        }

        return new Error('An unexpected error occurred');
    }
}

// Singleton instance — consumers should import this rather than `new`ing the class.
export const studioAIService = new StudioAIService();

export default StudioAIService;
